import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import useSplitText from '../useSplitText';
import { isColorDark, hexToRgba, resolveLinkProps, buildSrcSet, getTextAlign, getButtonSize } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import useLeadSubmit from '../useLeadSubmit';
import SectionDivider from './SectionDivider';

export default function CtaBanner({ block = {}, brand, mediaFolders = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const splitRef = useSplitText({ type: 'words', animation: 'fade-up', stagger: 0.05, disabled: !enableAnimations });
    const variant = config.variant || 'simple';

    const bgColor = config.bg_color || config.accent_color || primaryColor;
    const title = content.title || 'Comienza tu futuro hoy';
    const subtitle = content.subtitle || brand.slogan || 'No esperes mas para dar el siguiente paso';

    const dark = isColorDark(bgColor);
    const textColor = config.text_color || (dark ? '#ffffff' : '#111827');
    const textSubColor = config.text_color ? hexToRgba(config.text_color, 0.7) : (dark ? 'rgba(255,255,255,0.75)' : '#6b7280');

    // Dynamic buttons — backward compat
    const ctaButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : [
            ...(content.cta_text || brand.whatsapp ? [{ text: content.cta_text || (brand.whatsapp ? 'Escribenos por WhatsApp' : 'Contactanos ahora'), link: content.cta_link || (brand.whatsapp ? `https://wa.me/${brand.whatsapp.replace(/\D/g, '')}` : '#contact'), style: 'solid', icon: '', bg_color: '', text_color: '' }] : []),
            ...(content.cta2_text ? [{ text: content.cta2_text, link: content.cta2_link || '#contact', style: 'outline', icon: '', bg_color: '', text_color: '' }] : []),
        ];

    const padClass = config.padding === 'sm' ? 'py-12' : config.padding === 'lg' ? 'py-28' : 'py-20';
    const bgGradientTo = config.bg_gradient_to || hexToRgba(bgColor, 0.8);

    const Buttons = () => (
        <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((btn, i) => {
                const s = btn.style || (i === 0 ? 'solid' : 'outline');
                const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                const lp = resolveLinkProps(btn.link || '#');
                const customBg = btn.bg_color;
                const customText = btn.text_color;
                let cls = `${getButtonSize(config) || 'px-10 py-4'} rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 inline-flex items-center gap-2 `;
                let style = {};
                if (s === 'solid' || s === 'pill') {
                    if (customBg) {
                        style.backgroundColor = customBg;
                        style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827');
                        cls += 'shadow-lg hover:shadow-xl hover:opacity-90';
                    } else {
                        cls += dark ? 'bg-white shadow-lg hover:shadow-xl' : 'bg-gray-900 text-white shadow-lg hover:shadow-xl';
                        if (dark) style.color = bgColor;
                    }
                } else if (s === 'outline') {
                    if (customBg) {
                        style.color = customBg; style.borderColor = customBg;
                        cls += 'border-2 hover:opacity-80';
                    } else {
                        cls += 'border-2 hover:bg-white/10';
                        style.color = textColor; style.borderColor = hexToRgba(textColor, 0.25);
                    }
                } else { // ghost
                    cls += 'hover:bg-white/10';
                    style.color = customText || textColor;
                }
                if (customText && (s === 'solid' || s === 'pill')) style.color = customText;
                return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
            })}
        </div>
    );

    // ── Floating variant ──
    if (variant === 'floating') {
        return (
            <section className="py-12 md:py-20 px-4 md:px-8">
                <div ref={ref} className={`max-w-5xl mx-auto rounded-3xl ${padClass} relative overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ backgroundColor: bgColor }}>
                    <div className="absolute top-0 left-0 w-48 h-48 rounded-full bg-white/5 -translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full bg-white/5 translate-x-1/3 translate-y-1/3" />
                    <div className={`relative z-10 ${getTextAlign(config) || 'text-center'} px-6`}>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                        <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: textSubColor }}>{subtitle}</p>
                        <Buttons />
                    </div>
                </div>
            </section>
        );
    }

    // ── With image variant ──
    if (variant === 'with-image') {
        const folderImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
        const ctaImage = folderImages[0] || null;
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="text-center lg:text-left">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                            <p className="text-lg mb-10" style={{ color: textSubColor }}>{subtitle}</p>
                            <div className="lg:[&>div]:justify-start">
                                <Buttons />
                            </div>
                        </div>
                        <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                            {ctaImage?.image_url ? (
                                <img src={ctaImage.image_url} alt="" className="w-full rounded-2xl shadow-2xl object-cover max-h-[400px]" loading="lazy" decoding="async" srcSet={buildSrcSet(ctaImage)} sizes="(max-width:1024px) 100vw, 50vw"/>
                            ) : (
                                <div className="w-full h-64 rounded-2xl bg-white/10 flex items-center justify-center">
                                    <svg className="w-16 h-16 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Countdown variant ──
    if (variant === 'countdown') {
        const target = content.countdown_date ? new Date(content.countdown_date) : null;
        const now = new Date();
        const diff = target ? Math.max(0, target - now) : 0;
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        const units = [{v: days, l: 'Dias'}, {v: hours, l: 'Horas'}, {v: mins, l: 'Min'}, {v: secs, l: 'Seg'}];
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
                <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                    <p className="text-lg mb-8" style={{ color: textSubColor }}>{subtitle}</p>
                    {target && (
                        <div className="flex justify-center gap-2 sm:gap-4 mb-10">
                            {units.map((u, i) => (
                                <div key={i} className="bg-white/10 rounded-xl px-4 py-3 min-w-[60px] sm:min-w-[70px]">
                                    <div className="text-3xl font-extrabold" style={{ color: textColor }}>{u.v}</div>
                                    <div className="text-[10px] uppercase tracking-wider" style={{ color: textSubColor }}>{u.l}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <Buttons />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Split color variant ──
    if (variant === 'split-color') {
        const bg2 = bgGradientTo;
        return (
            <section ref={ref} className={`relative overflow-hidden`}>
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className={`${padClass} flex items-center justify-center px-8`} style={{ backgroundColor: bgColor }}>
                        <div className={`text-center transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                            <h2 className="text-3xl font-bold mb-3" style={{ color: textColor }}>{title}</h2>
                            <p className="text-base" style={{ color: textSubColor }}>{subtitle}</p>
                        </div>
                    </div>
                    <div className={`${padClass} flex items-center justify-center px-8`} style={{ backgroundColor: bg2 }}>
                        <div className={`text-center transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                            <Buttons />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // ── Banner strip variant ──
    if (variant === 'banner-strip') {
        return (
            <section ref={ref} className="relative overflow-hidden" style={{ backgroundColor: bgColor }}>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="flex items-center gap-3">
                        <h3 className="text-sm font-bold" style={{ color: textColor }}>{title}</h3>
                        {subtitle && <span className="text-sm" style={{ color: textSubColor }}>— {subtitle}</span>}
                    </div>
                    <div className="shrink-0 [&>div]:gap-3 [&_a]:px-5 [&_a]:py-2 [&_a]:text-xs">
                        <Buttons />
                    </div>
                </div>
            </section>
        );
    }

    // ── Gradient animated variant ──
    if (variant === 'gradient-animated') {
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`}>
                <div className="absolute inset-0 animate-gradient-shift" style={{ background: `linear-gradient(270deg, ${bgColor}, ${bgGradientTo}, ${bgColor})`, backgroundSize: '200% 200%' }} />
                <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-white/5 -translate-x-1/3 -translate-y-1/3" />
                <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-white/5 translate-x-1/3 translate-y-1/3" />
                <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                    <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: textSubColor }}>{subtitle}</p>
                    <Buttons />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <style>{`
                    @keyframes gradient-shift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
                    .animate-gradient-shift { animation: gradient-shift 6s ease infinite; }
                `}</style>
            </section>
        );
    }

    // ── Checklist variant ──
    if (variant === 'checklist') {
        const benefits = (content.benefits || '').split('\n').filter(b => b.trim());
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
                <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-white/5 -translate-x-1/3 -translate-y-1/3" />
                <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${getTextAlign(config) || 'text-center'} transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                    {subtitle && <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: textSubColor }}>{subtitle}</p>}
                    {benefits.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-10">
                            {benefits.map((b, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{color: dark ? '#ffffff' : bgColor}}>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                    </svg>
                                    <span className="text-sm font-medium" style={{ color: textColor }}>{b.trim()}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <Buttons />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Metric Burst variant ──
    if (variant === 'metric-burst') {
        const metricItems = content.metrics || [];
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{backgroundColor: bgColor}}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    {metricItems.length > 0 && (
                        <div className={`grid grid-cols-${Math.min(metricItems.length, 4)} gap-4 mb-8`}>
                            {metricItems.map((m, i) => (
                                <div key={i}>
                                    <span className={`text-4xl sm:text-5xl font-extrabold block ${dark ? 'text-white' : ''}`}>{m.value}</span>
                                    <span className={`text-sm ${dark ? 'text-white/60' : 'text-white/70'}`}>{m.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={`w-full h-px mb-8 ${dark ? 'bg-white/15' : 'bg-white/20'}`}/>
                    <h2 ref={splitRef} className={`text-2xl sm:text-4xl font-bold leading-tight mb-4 ${dark ? 'text-white' : 'text-white'}`}>{title}</h2>
                    {subtitle && <p className={`text-base sm:text-lg max-w-xl mx-auto mb-8 ${dark ? 'text-white/70' : 'text-white/80'}`}>{subtitle}</p>}
                    <Buttons/>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Embed Form variant ──
    if (variant === 'embed-form') {
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl">
                        <div className="p-8 sm:p-10 flex flex-col justify-center" style={{backgroundColor: bgColor}}>
                            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{title}</h2>
                            {subtitle && <p className="text-white/80 mb-6">{subtitle}</p>}
                            <ul className="space-y-2">
                                {(content.checklist || ['Sin compromiso', 'Respuesta en 24h', 'Soporte incluido']).map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                                        <svg className="w-4 h-4 text-white/60 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                                        {typeof item === 'string' ? item : item.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-8 sm:p-10 bg-white flex flex-col justify-center">
                            <div className="space-y-4">
                                {(content.form_fields || ['Nombre', 'Email', 'Empresa']).map((field, i) => (
                                    <div key={i}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{typeof field === 'string' ? field : field.label}</label>
                                        <input type="text" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none" placeholder={typeof field === 'string' ? field : field.placeholder}/>
                                    </div>
                                ))}
                                <button className="w-full py-3 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition-opacity" style={{backgroundColor: bgColor}}>{content.cta_text || 'Enviar'}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Cinematic Video variant ──
    if (variant === 'cinematic-video') {
        return (
            <section ref={ref} className="relative overflow-hidden" style={{minHeight: '80vh'}}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                {content.video_url && <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover"><source src={content.video_url}/></video>}
                <div className="absolute inset-0" style={{backgroundColor: hexToRgba(bgColor, 0.7)}}/>
                <div className="relative z-10 flex items-center justify-center px-4" style={{minHeight: '80vh'}}>
                    <div className="rounded-3xl p-8 sm:p-12 text-center max-w-xl" style={{backgroundColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.15)'}}>
                        {content.badge && <span className="inline-block text-xs font-bold uppercase px-3 py-1 rounded-full text-white mb-4 bg-white/20">{content.badge}</span>}
                        <h2 ref={splitRef} className="text-3xl sm:text-4xl font-bold text-white mb-4">{title}</h2>
                        {subtitle && <p className="text-white/80 mb-8">{subtitle}</p>}
                        <Buttons/>
                        {content.caption && <p className="text-xs text-white/50 mt-4">{content.caption}</p>}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Social Proof Wall variant ──
    if (variant === 'social-proof-wall') {
        const proofItems = content.proof_items || [];
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{backgroundColor: bgColor}}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-[3fr_2fr] gap-8 items-center">
                        <div>
                            <h2 ref={splitRef} className="text-2xl sm:text-4xl font-bold text-white mb-4">{title}</h2>
                            {subtitle && <p className="text-white/80 mb-6">{subtitle}</p>}
                            <Buttons/>
                            <div className="flex items-center gap-2 mt-6">
                                <div className="flex -space-x-2">{[0,1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white/20 bg-white/10 flex items-center justify-center text-[10px] text-white font-bold">{['A','B','C','D'][i]}</div>)}</div>
                                <span className="text-sm text-white/60">{content.proof_text || 'Confiado por miles'}</span>
                            </div>
                        </div>
                        {proofItems.length > 0 && (
                            <div className="space-y-3">
                                {proofItems.map((p, i) => (
                                    <div key={i} className="rounded-xl p-4 bg-white/10 backdrop-blur-sm border border-white/10">
                                        <p className="text-sm text-white/90 italic">"{p.quote}"</p>
                                        <p className="text-xs text-white/50 mt-2">{p.name}{p.role ? `, ${p.role}` : ''}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Social Proof variant ──
    if (variant === 'social-proof') {
        const folderImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
        const avatarColors = [primaryColor, secondaryColor, hexToRgba(primaryColor, 0.7), hexToRgba(secondaryColor, 0.7), primaryColor];
        const socialCount = content.social_count || '10,000';
        const starRating = content.star_rating || '4.9';
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                    {subtitle && <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: textSubColor }}>{subtitle}</p>}
                    <div className="flex items-center justify-center mb-4">
                        <div className="flex -space-x-3">
                            {[0,1,2,3,4].map(i => {
                                const img = folderImages[i];
                                return img?.image_url ? (
                                    <img key={i} src={img.image_url} alt="" className="w-10 h-10 rounded-full border-2 object-cover" style={{ borderColor: bgColor }} loading="lazy" decoding="async"/>
                                ) : (
                                    <div key={i} className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-xs font-bold text-white" style={{ borderColor: bgColor, background: avatarColors[i] }}>
                                        {['A','B','C','D','E'][i]}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <p className="text-sm font-medium mb-2" style={{ color: textSubColor }}>
                        Únete a {socialCount}+ usuarios que confían en nosotros
                    </p>
                    <div className="flex items-center justify-center gap-1.5 mb-8">
                        {[0,1,2,3,4].map(i => (
                            <svg key={i} className="w-5 h-5" viewBox="0 0 20 20" fill={i < Math.floor(parseFloat(starRating)) ? '#facc15' : (i < Math.ceil(parseFloat(starRating)) ? '#facc15' : hexToRgba(textColor, 0.2))}>
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                            </svg>
                        ))}
                        <span className="text-sm font-semibold ml-1" style={{ color: textColor }}>{starRating}/5</span>
                    </div>
                    <Buttons />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Inline Form variant ──
    if (variant === 'inline-form') {
        const { submitting, submitted, error: leadError, submit: leadSubmit } = useLeadSubmit({
            brandId: brand?.id, blockType: 'cta', blockId: block.id, pageId: block.page_id,
        });
        const [email, setEmail] = useState('');
        const handleSubmit = (e) => {
            e.preventDefault();
            if (!email.trim()) return;
            leadSubmit({ email });
        };
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    {submitted ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-4">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(textColor, 0.1) }}>
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: textColor }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                            </div>
                            <p className="text-lg font-bold" style={{ color: textColor }}>¡Gracias!</p>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                            <div className="flex-1 text-center md:text-left">
                                <h2 className="text-2xl md:text-3xl font-bold" style={{ color: textColor }}>{title}</h2>
                            </div>
                            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                                <input
                                    type="email" required value={email} onChange={e => setEmail(e.target.value)}
                                    placeholder="Tu email" className="px-5 py-3 rounded-full text-sm border-0 outline-none min-w-[220px]"
                                    style={{ backgroundColor: hexToRgba(textColor, 0.1), color: textColor }}
                                />
                                <button type="submit" disabled={submitting}
                                    className="px-8 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 hover:shadow-lg disabled:opacity-60"
                                    style={{ backgroundColor: dark ? '#ffffff' : '#111827', color: dark ? bgColor : '#ffffff' }}>
                                    {submitting ? '...' : (ctaButtons[0]?.text || 'Suscribirse')}
                                </button>
                            </form>
                        </div>
                    )}
                    {!submitted && (
                        <p className="text-xs mt-4 text-center md:text-right" style={{ color: textSubColor }}>
                            {content.trust_text || 'Sin spam. Cancela cuando quieras.'}
                        </p>
                    )}
                    {leadError && <p className="text-xs text-red-400 mt-2 text-center md:text-right">{leadError}</p>}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Risk Reversal variant ──
    if (variant === 'risk-reversal') {
        const defaultGuarantees = ['Garantía de 30 días', 'Sin tarjeta de crédito', 'Soporte 24/7', 'Cancela cuando quieras'];
        const guaranteeItems = content.guarantees ? content.guarantees.split('\n').filter(g => g.trim()) : defaultGuarantees;
        const folderImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
        return (
            <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                    {subtitle && <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: textSubColor }}>{subtitle}</p>}
                    <Buttons />
                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mt-8">
                        {guaranteeItems.map((g, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <svg className="w-5 h-5 shrink-0 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                                </svg>
                                <span className="text-sm font-medium" style={{ color: textSubColor }}>{g.trim()}</span>
                            </div>
                        ))}
                    </div>
                    {folderImages.length > 0 && (
                        <div className="flex flex-wrap justify-center items-center gap-6 mt-10 opacity-50">
                            {folderImages.slice(0, 6).map((img, i) => (
                                <img key={i} src={img.image_url} alt="" className="h-8 object-contain grayscale" loading="lazy" decoding="async"/>
                            ))}
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Dual CTA variant (new vs returning users) ──
    if (variant === 'dual-cta') {
        const leftTitle = content.left_title || title;
        const leftSubtitle = content.left_subtitle || 'Crea tu cuenta gratis y empieza hoy';
        const rightTitle = content.right_title || '¿Ya tienes una cuenta?';
        const rightSubtitle = content.right_subtitle || 'Inicia sesion para continuar';
        return (
            <section ref={ref} className={`relative overflow-hidden`}>
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className={`${padClass} flex flex-col items-center justify-center px-8 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                        style={{ backgroundColor: bgColor }}>
                        <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: textColor }}>{leftTitle}</h2>
                        <p className="text-base mb-6 max-w-md" style={{ color: textSubColor }}>{leftSubtitle}</p>
                        {ctaButtons[0] && (() => {
                            const btn = ctaButtons[0];
                            const lp = resolveLinkProps(btn.link || '#');
                            return (
                                <a {...lp} className={`${getButtonSize(config) || 'px-10 py-4'} rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl inline-flex items-center gap-2`}
                                    style={{ backgroundColor: dark ? '#ffffff' : '#111827', color: dark ? bgColor : '#ffffff' }}>
                                    {btn.icon && getButtonIcon(resolveIconKey(btn.icon))}
                                    {btn.text}
                                </a>
                            );
                        })()}
                    </div>
                    <div className={`${padClass} flex flex-col items-center justify-center px-8 text-center transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                        style={{ backgroundColor: hexToRgba(bgColor, 0.85) }}>
                        <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: textColor }}>{rightTitle}</h2>
                        <p className="text-base mb-6 max-w-md" style={{ color: textSubColor }}>{rightSubtitle}</p>
                        {ctaButtons[1] && (() => {
                            const btn = ctaButtons[1];
                            const lp = resolveLinkProps(btn.link || '#');
                            return (
                                <a {...lp} className={`${getButtonSize(config) || 'px-10 py-4'} rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 border-2 inline-flex items-center gap-2`}
                                    style={{ color: textColor, borderColor: hexToRgba(textColor, 0.3) }}>
                                    {btn.icon && getButtonIcon(resolveIconKey(btn.icon))}
                                    {btn.text}
                                </a>
                            );
                        })()}
                    </div>
                </div>
            </section>
        );
    }

    // ── Simple (default) ──
    return (
        <section ref={ref} className={`relative ${padClass} overflow-hidden`} style={{ backgroundColor: bgColor }}>
            <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-white/5 -translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-white/5 translate-x-1/3 translate-y-1/3" />
            <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${getTextAlign(config) || 'text-center'} transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2 ref={splitRef} className="text-3xl md:text-4xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
                <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: textSubColor }}>{subtitle}</p>
                <Buttons />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
